import React, { useState, useRef, useEffect } from 'react';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import MessageBubble from './MessageBubble';
import FormSelector from './FormSelector';
import FormEditor from './FormEditor';
import {
  sendMessage, selectForm, submitField, searchForms,
  generateDocument, FormInfo, FieldInfo,
  generateHwpDocument, IntegratedGenerateResponse,
  getIntegratedDownloadUrl, getFormTemplate, aiEditForm,
  generateCanvasHwp, FormTemplateResponse
} from '../services/api';
import './ChatWindow.css';

// API 호스트 URL (다운로드 등에 사용)
const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1', '');

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  type?: string;
  forms?: FormInfo[];
  fieldInfo?: FieldInfo;
  progress?: { current: number; total: number; percent: number };
  downloadUrl?: string;
  nextAction?: string;
  integratedResult?: IntegratedGenerateResponse;
}

// 일반회원용 예시 질문 카드
const GENERAL_PROMPTS = [
  { text: '소장·고소장은 어떻게 작성하나요?' },
  { text: '내용증명·준비서면·답변서를 작성하고 싶어요' },
  { text: '가압류·가처분·경매신청서를 작성하여 주세요' },
  { text: '이혼·상속·후견·재산분할·가족관계등록부 서식을 작성하고 싶어요' },
  { text: '등기·공탁 신청서를 작성하여 주세요' },
];

// 전문가용 15개 카테고리 버튼 (query: 실제 검색에 사용되는 키워드)
const PRO_CATEGORIES = [
  { text: '헌법서식작성', query: '헌법', color: '#a04040' },
  { text: '민사서식작성', query: '민사', color: '#3366aa' },
  { text: '형사서식작성', query: '형사', color: '#cc4444' },
  { text: '가사서식작성', query: '가사', color: '#cc6688' },
  { text: '특허서식작성', query: '특허', color: '#7755bb' },
  { text: '행정서식작성', query: '행정', color: '#338855' },
  { text: '집행서식작성', query: '집행', color: '#335577' },
  { text: '신청서식작성', query: '신청', color: '#33aaaa' },
  { text: '회생서식작성', query: '회생', color: '#cc8844' },
  { text: '파산서식작성', query: '파산', color: '#886644' },
  { text: '등기서식작성', query: '등기', color: '#448888' },
  { text: '가족서식작성', query: '가족', color: '#cc8888' },
  { text: '소년서식작성', query: '소년', color: '#888855' },
  { text: '공탁서식작성', query: '공탁', color: '#884488' },
  { text: '비송서식작성', query: '비송', color: '#558888' },
];

const ChatWindow: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentState, setCurrentState] = useState<string>('welcome');
  const [currentField, setCurrentField] = useState<FieldInfo | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // URL 파라미터에서 회원 유형 읽기 (?type=professional|master)
  const [memberType] = useState<'general' | 'professional' | 'master'>(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type === 'master') return 'master';
    if (type === 'professional') return 'professional';
    return 'general';
  });
  const isProfessional = memberType === 'professional' || memberType === 'master';
  const isMaster = memberType === 'master';

  // Canvas 스타일: 서식 에디터 상태
  const [editorContent, setEditorContent] = useState<string>('');
  const [selectedFormName, setSelectedFormName] = useState<string>('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isEditorLoading, setIsEditorLoading] = useState(false);
  const [editorDownloadUrl, setEditorDownloadUrl] = useState<string | null>(null);

  // 입력 모드: 에디터 열린 상태에서 채팅/편집 전환
  const [inputMode, setInputMode] = useState<'chat' | 'edit'>('chat');

  // 통합 파이프라인용 상태
  const [collectedData, setCollectedData] = useState<Record<string, any>>({});
  const [caseDescription, setCaseDescription] = useState<string>('');

  // 화면 크기 감지
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 820);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 820);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (customInput?: string, displayText?: string) => {
    const messageText = customInput || input;
    if (!messageText.trim() || isLoading) return;

    // 사용자 메시지 표시 (displayText가 있으면 표시용으로 사용)
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: displayText || messageText
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setCaseDescription(messageText);

    try {
      // [1] 웰컴 → 법률 안내문 + LLM 응답 + 서식 검색 동시 실행
      if (currentState === 'welcome') {
        // 법률 안내문 먼저 표시
        const guideMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: '',
          type: 'legal_guide'
        };
        setMessages(prev => [...prev, guideMessage]);

        // LLM 대화 + 서식 검색 동시 실행 (마스터: 전문가로 대화 + 일반+전문가 동시검색)
        const chatType = isMaster ? 'professional' : memberType;
        const promises: Promise<any>[] = [
          sendMessage(messageText, sessionId || undefined, chatType).catch(() => null),
          searchForms(messageText, undefined, 10, isMaster ? 'general' : memberType).catch(() => null)
        ];
        if (isMaster) {
          promises.push(searchForms(messageText, undefined, 10, 'professional').catch(() => null));
        }
        const promiseResults = await Promise.all(promises);
        const chatResult = promiseResults[0];
        const searchResult = promiseResults[1];
        const proSearchResult = isMaster ? promiseResults[2] : null;

        if (chatResult?.session_id) {
          setSessionId(chatResult.session_id);
        }

        // LLM 응답 표시
        const llmMessage = chatResult?.response?.message || '';
        if (llmMessage) {
          const aiMsg: ChatMessage = {
            id: (Date.now() + 2).toString(),
            role: 'assistant',
            content: llmMessage
          };
          setMessages(prev => [...prev, aiMsg]);
        }

        // 서식 카드 표시 (마스터: 일반+전문가 통합)
        const llmForms = chatResult?.response?.forms || [];
        const generalForms = searchResult?.results || [];
        const proForms = proSearchResult?.results || [];
        const allSearchForms = [...generalForms, ...proForms];
        const seen = new Set<string>();
        const uniqueForms = allSearchForms.filter((f: FormInfo) => {
          if (seen.has(f.form_id)) return false;
          seen.add(f.form_id);
          return true;
        });
        const forms = uniqueForms.length > 0 ? uniqueForms : llmForms;

        if (forms.length > 0) {
          const formMessage: ChatMessage = {
            id: (Date.now() + 3).toString(),
            role: 'assistant',
            content: `관련 서식 ${forms.length}개를 찾았습니다. 원하시는 서식을 **클릭**하면 바로 편집할 수 있습니다.`,
            type: 'form_list',
            forms: forms
          };
          setMessages(prev => [...prev, formMessage]);
          setCurrentState('form_selection');
        } else {
          setCurrentState('chatting');
        }

      // [2] 에디터 열려있고 편집 모드일 때 → AI 전체 편집 라우팅
      } else if (inputMode === 'edit' && isEditorOpen && selectedFormId && editorContent) {
        // Canvas가 열린 상태에서 하단 입력 → AI 전체 편집으로 라우팅
        setIsEditorLoading(true);
        try {
          const result = await aiEditForm(selectedFormId, {
            selection: '',
            instruction: messageText,
            full_content: editorContent
          });

          const actuallyChanged = result.suggested_text && result.suggested_text !== editorContent;
          const isReliable = result.confidence >= 0.5;

          if (actuallyChanged && isReliable) {
            setEditorContent(result.suggested_text);
            setEditorDownloadUrl(null);

            const responseMessage: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: `AI가 서식을 수정했습니다.\n\n편집기에서 내용을 확인하고, 수정이 필요하면 추가 요청해주세요.`,
              type: 'ai_edit_complete'
            };
            setMessages(prev => [...prev, responseMessage]);
          } else if (actuallyChanged && !isReliable) {
            setEditorContent(result.suggested_text);
            setEditorDownloadUrl(null);

            const responseMessage: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: `AI가 수정을 시도했으나 정확도가 낮습니다. 편집기에서 결과를 꼭 확인해주세요.\n\n원하는 대로 수정되지 않았다면 직접 편집하거나, 더 구체적으로 요청해주세요.`,
              type: 'ai_edit_complete'
            };
            setMessages(prev => [...prev, responseMessage]);
          } else {
            const responseMessage: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: '수정할 부분을 찾지 못했습니다. 더 구체적으로 요청해주세요.\n\n예: "고소인 이름을 김철수로 변경" 또는 직접 편집기에서 수정할 수 있습니다.',
              type: 'error'
            };
            setMessages(prev => [...prev, responseMessage]);
          }
        } catch (error: any) {
          const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: `AI 수정 중 오류: ${error.response?.data?.detail || error.message}`,
            type: 'error'
          };
          setMessages(prev => [...prev, errorMessage]);
        } finally {
          setIsEditorLoading(false);
        }

      // [3] 일반 채팅
      } else {
        // LLM 대화와 서식 검색을 동시에 실행 (마스터: 전문가로 대화 + 일반+전문가 동시검색)
        const chatType = isMaster ? 'professional' : memberType;
        const promises3: Promise<any>[] = [
          sendMessage(messageText, sessionId || undefined, chatType).catch(() => null),
          searchForms(messageText, undefined, 10, isMaster ? 'general' : memberType).catch(() => null)
        ];
        if (isMaster) {
          promises3.push(searchForms(messageText, undefined, 10, 'professional').catch(() => null));
        }
        const results3 = await Promise.all(promises3);
        const chatResult = results3[0];
        const searchResult = results3[1];
        const proSearchResult3 = isMaster ? results3[2] : null;

        // 세션 ID 저장
        if (chatResult?.session_id) {
          setSessionId(chatResult.session_id);
        }

        // 서식 목록 먼저 확인 (마스터: 일반+전문가 통합)
        const llmForms = chatResult?.response?.forms || [];
        const generalForms3 = searchResult?.results || [];
        const proForms3 = proSearchResult3?.results || [];
        const allSearchForms3 = [...generalForms3, ...proForms3];
        const seen3 = new Set<string>();
        const uniqueForms3 = allSearchForms3.filter((f: FormInfo) => {
          if (seen3.has(f.form_id)) return false;
          seen3.add(f.form_id);
          return true;
        });
        const forms = uniqueForms3.length > 0 ? uniqueForms3 : llmForms;

        // LLM 응답: 검색 결과가 있으면 생략 (상태머신 꼬인 응답 방지)
        const llmMessage = chatResult?.response?.message || '';
        if (llmMessage && forms.length === 0) {
          const aiMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: llmMessage
          };
          setMessages(prev => [...prev, aiMessage]);
        }

        if (forms.length > 0) {
          const formMessage: ChatMessage = {
            id: (Date.now() + 2).toString(),
            role: 'assistant',
            content: `관련 서식 ${forms.length}개를 찾았습니다. 원하시는 서식을 **클릭**하면 바로 편집할 수 있습니다.`,
            type: 'form_list',
            forms: forms
          };
          setMessages(prev => [...prev, formMessage]);
          setCurrentState('form_selection');
        }
      }
    } catch (error: any) {
      console.error('처리 오류:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `오류가 발생했습니다: ${error.response?.data?.detail || error.message || '다시 시도해주세요.'}`,
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (text: string, displayText?: string) => {
    handleSend(text, displayText);
  };

  // Canvas 스타일: 서식 선택 시 바로 에디터에 표시
  const handleFormSelect = async (formId: string, formName?: string) => {
    setSelectedFormId(formId);
    setIsEditorLoading(true);
    setIsEditorOpen(true);
    setInputMode('edit');

    try {
      // 실제 API에서 서식 템플릿 HTML 가져오기 (formName으로 fallback 검색 지원)
      const template = await getFormTemplate(formId, formName);
      setEditorContent(template.content_html);
      setSelectedFormName(template.form_name);

      // 서식 선택 확인 메시지
      const confirmMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `**${template.form_name}** 서식이 편집기에 표시되었습니다.\n\n• 직접 편집하거나\n• 텍스트를 선택 후 "AI 수정" 버튼을 클릭하면 AI가 수정해드립니다.\n\n**편집 가능한 필드**: ${template.editable_fields.slice(0, 5).join(', ')}${template.editable_fields.length > 5 ? ' 외' : ''}`,
        type: 'form_loaded'
      };
      setMessages(prev => [...prev, confirmMessage]);
    } catch (error: any) {
      console.error('서식 로드 오류:', error);
      setIsEditorOpen(false);

      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `서식을 불러올 수 없습니다. 다른 서식을 선택해주세요.\n\n오류: ${error.response?.data?.detail || error.message}`,
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsEditorLoading(false);
    }
  };

  // AI 부분 수정 요청
  const handleAIEditRequest = async (selection: string, instruction: string) => {
    if (!selectedFormId) return;

    setIsEditorLoading(true);

    const requestMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: `"${selection.substring(0, 50)}${selection.length > 50 ? '...' : ''}" 부분을 "${instruction}"로 수정 요청`
    };
    setMessages(prev => [...prev, requestMessage]);

    try {
      // 실제 AI 수정 API 호출
      const result = await aiEditForm(selectedFormId, {
        selection,
        instruction,
        full_content: editorContent
      });

      // 에디터 내용에서 선택 텍스트를 AI 제안으로 교체
      if (result.suggested_text && result.confidence > 0) {
        const updatedContent = editorContent.replace(selection, result.suggested_text);
        setEditorContent(updatedContent);
        setEditorDownloadUrl(null); // 편집 후 이전 HWP URL 무효화

        const responseMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `AI가 선택한 부분을 수정했습니다.\n\n**변경 전**: "${selection.substring(0, 100)}${selection.length > 100 ? '...' : ''}"\n**변경 후**: "${result.suggested_text.substring(0, 100)}${result.suggested_text.length > 100 ? '...' : ''}"\n\n수정 신뢰도: ${(result.confidence * 100).toFixed(0)}%`,
          type: 'ai_edit_complete'
        };
        setMessages(prev => [...prev, responseMessage]);
      } else {
        const responseMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'AI 수정에 실패했습니다. 다른 지시로 다시 시도해주세요.',
          type: 'error'
        };
        setMessages(prev => [...prev, responseMessage]);
      }
    } catch (error: any) {
      console.error('AI 수정 오류:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `AI 수정 중 오류가 발생했습니다: ${error.response?.data?.detail || error.message}`,
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsEditorLoading(false);
    }
  };

  // Canvas 스타일: HWP 생성 + 다운로드 통합 (편집된 HTML 반영)
  const handleEditorDownload = async () => {
    // 이미 생성된 URL이 있으면 바로 다운로드
    if (editorDownloadUrl) {
      window.open(`${API_HOST}${editorDownloadUrl}`, '_blank');
      return;
    }

    if (!selectedFormId) return;
    setIsEditorLoading(true);

    try {
      // Canvas 편집 내용 → HWP 변환 (원본과 비교하여 변경사항 반영)
      const result = await generateCanvasHwp(selectedFormId, editorContent);

      if (result.success && result.download_url) {
        setEditorDownloadUrl(result.download_url);
        window.open(`${API_HOST}${result.download_url}`, '_blank');

        const successMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `HWP 문서가 생성되어 다운로드됩니다.\n\n파일명: ${result.filename}`,
          type: 'download_ready',
          downloadUrl: result.download_url
        };
        setMessages(prev => [...prev, successMessage]);
      }
    } catch (error: any) {
      console.error('문서 생성 오류:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `문서 생성 중 오류: ${error.message}`,
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsEditorLoading(false);
    }
  };

  const handleFieldSubmit = async (value: string) => {
    if (!sessionId || !currentField) return;
    setIsLoading(true);

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: value
    };
    setMessages(prev => [...prev, userMessage]);

    setCollectedData(prev => ({
      ...prev,
      [currentField.name]: value
    }));

    if (currentField.name.includes('설명') || currentField.name.includes('개요') || currentField.name.includes('내용')) {
      setCaseDescription(prev => prev + ' ' + value);
    }

    try {
      const response = await submitField(sessionId, currentField.name, value);
      setCurrentState(response.state);

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.response.message,
        type: response.response.type,
        fieldInfo: response.response.current_field,
        progress: response.response.progress,
        nextAction: response.response.next_action
      };
      setMessages(prev => [...prev, aiMessage]);

      if (response.response.current_field) {
        setCurrentField(response.response.current_field);
      } else {
        setCurrentField(null);
      }

      if (response.response.next_action === 'generate') {
        await handleGenerateDocument();
      }
    } catch (error) {
      console.error('필드 제출 오류:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDocument = async () => {
    if (!sessionId || !selectedFormId) return;
    setIsLoading(true);

    const processingMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: 'HWP 문서를 생성하고 있습니다... 잠시만 기다려주세요.',
      type: 'processing'
    };
    setMessages(prev => [...prev, processingMessage]);

    try {
      const result = await generateHwpDocument(
        selectedFormId,
        collectedData,
        caseDescription,
        true,
        sessionId
      );

      setMessages(prev => prev.filter(m => m.id !== processingMessage.id));

      if (result.success) {
        let contentParts = [
          '문서가 성공적으로 생성되었습니다!',
          '',
          `파일명: ${result.filename}`,
          `서식: ${result.form_name}`,
          `교체 항목: ${result.replacements_count}개`,
        ];

        if (result.mode === 'llm') {
          contentParts.push(`신뢰도: ${(result.confidence * 100).toFixed(0)}%`);
          if (result.requirements_count > 0) {
            contentParts.push(`요건사실: ${result.requirements_fulfilled}/${result.requirements_count} 충족`);
          }
        } else {
          contentParts.push('(Fallback 모드로 생성됨)');
        }

        if (result.warnings && result.warnings.length > 0) {
          contentParts.push('');
          contentParts.push('주의사항:');
          result.warnings.forEach(w => contentParts.push(`- ${w}`));
        }

        contentParts.push('');
        contentParts.push('아래 버튼을 클릭하여 다운로드하세요.');

        const successMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: contentParts.join('\n'),
          type: 'download_ready',
          downloadUrl: result.download_url,
          integratedResult: result
        };
        setMessages(prev => [...prev, successMessage]);
        setCurrentState('download');
        setCurrentField(null);
      } else {
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `문서 생성에 실패했습니다.\n\n오류: ${result.errors?.join(', ') || '알 수 없는 오류'}`,
          type: 'error'
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error: any) {
      setMessages(prev => prev.filter(m => m.id !== processingMessage.id));
      console.error('문서 생성 오류:', error);

      try {
        const result = await generateDocument(sessionId);
        const successMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `문서가 생성되었습니다!\n\n파일명: ${result.filename}\n서식: ${result.form_name}`,
          type: 'download_ready',
          downloadUrl: result.download_url
        };
        setMessages(prev => [...prev, successMessage]);
        setCurrentState('download');
        setCurrentField(null);
      } catch (fallbackError: any) {
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `문서 생성 중 오류가 발생했습니다: ${error.response?.data?.detail || error.message}`,
          type: 'error'
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDownload = (url: string) => {
    window.open(`${API_HOST}${url}`, '_blank');
  };

  const handleNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setSelectedFormId(null);
    setCurrentState('welcome');
    setCurrentField(null);
    setInput('');
    setCollectedData({});
    setCaseDescription('');
    setEditorContent('');
    setSelectedFormName('');
    setIsEditorOpen(false);
    setEditorDownloadUrl(null);
    setInputMode('chat');
  };

  const getLegalGuideHTML = () => {
    return `
<h2 class="guide-title">1. 법률서식 종류</h2>
<p class="guide-intro">아래와 같이 각 과목에 따른 법률서식은 일반적으로 빈번하게 사용되고 있는 법률서식의 종류로서 권리관계를 확정하고 당사자 사이에 발생한 분쟁에 대하여 법률서식의 핵심 구성 요소와 작성 요령을 정리하여 드리는 바, 구체적으로 필요한 서식을 맞춤형 초안으로 작성하여 드릴 수 있습니다. 로콜 AI&LAWFORM에서 제공해 드리는 서식 초안은 참고용이며, 실제 법적 효력을 완벽히 보장하거나 구체적인 개별 사건의 승소를 담보하지는 않습니다. 따라서 사건이 복잡한 경우 반드시 법률전문가회원의 검토를 받으시기를 권장합니다.</p>

<div class="guide-categories">
<p><span class="cat-title">헌법관련:</span> 헌법소원심판, 정당해산심판, 권한쟁의심판, 위헌법률심판 등</p>
<p><span class="cat-title">민사관련:</span> 소장, 지급명령신청, 계약서, 답변서, 준비서면, 내용증명, 항소(상고)장 등</p>
<p><span class="cat-title">형사관련:</span> 고소(발)장, 체포·구속적부심사청구, 보석청구, 약식재판청구, 정식재판청구, 보석청구, 합의서, 탄원서, 항소장 등</p>
<p><span class="cat-title">가사관련:</span> 이혼, 친권행사, 재산분할, 후견인, 실종선고, 인지, 사실혼, 친생자부존재 등</p>
<p><span class="cat-title">행정관련:</span> 감봉·견책·과징금·등록세·부가가치세·영업정지·운전면허·종합소득세취소 등</p>
<p><span class="cat-title">특허관련:</span> 거절결정·등록무효·정정심결취소 등</p>
<p><span class="cat-title">신청관련:</span> 부동산·유체동산·채권가압류·가처분, 권리행사최고·담보취소·제소명령, 제소전화해, 가압류·가처분집행해제, 소송비용확정 등</p>
<p><span class="cat-title">회생관련:</span> 개인·법인회생개시, 면책신청, 개인회생채권자목록 등</p>
<p><span class="cat-title">파산관련:</span> 개인파산, 면책신청 등</p>
<p><span class="cat-title">집행관련:</span> 부동산·건설기계·선박·자동차강제(임의)경매, 재산명시, 채권압류·추심·전부, 제3자이의·청구이의, 채무불이행자명부 등</p>
<p><span class="cat-title">등기관련:</span> 사단·재단등기, 부동산·동산등기, 법인등기, 특수법인등기 등</p>
<p><span class="cat-title">가족관련:</span> 가족관계등록부정정신·정정, 성·본창설, 개명, 협의이혼 등</p>
<p><span class="cat-title">소년관련:</span> 긴급임시조치, 아동보호배상명령, 피해자아동보호, 아동보호배상명령 등</p>
<p><span class="cat-title">공탁관련:</span> 변제(금전)공탁, 보증공탁, 집행공탁, 혼합공탁, 공탁서정정, 공탁금출급·회수 등</p>
<p><span class="cat-title">비송관련:</span> 주차위반이의, 공탁관불수리처분이의 등</p>
</div>

<h2 class="guide-title">2. 법률서식 작성</h2>
<p class="guide-content">위 각종 사건에 따른 법률문서는 6하원칙에 의하여 작성하되, 그 내용 및 주의사항으로서 이름, 생년월일(주민등록번호), 주소, 연락처를 정확히 기재(법인은 명칭과 대표자)하여야 하고, 또한 청구취지 및 청구원인, 사건의 경위 등을 날짜별로 사실관계 기입하여 기재한 다음, 구체적인 증거 자료, 즉 차용증, 입금증, 문자·카톡 캡처, 녹취서, 계약서, 사진, 동영상 등 자신의 주장을 뒷받침할 자료를 첨부한 후, 작성 연월일을 기재하고 날인 또는 서명을 하여야 합니다.</p>
`;
  };

  // 웰컴 화면
  const renderWelcomeScreen = () => (
    <div className="welcome-screen">
      <div className="welcome-content">
        <p className="welcome-brand">로콜 AI&amp;LAWFORM</p>
        <p className="welcome-subtitle">
          {isProfessional ? '스탠다드(WebChatbot)' : '일반회원 전용(무료)'}
        </p>
        <h1 className="welcome-title">무엇을 도와드릴까요?</h1>

        {isProfessional ? (
          <div className="pro-category-grid">
            {PRO_CATEGORIES.map((item, index) => (
              <button
                key={index}
                className="pro-category-btn"
                onClick={() => handleExampleClick(item.query, item.text)}
              >
                {item.text}
              </button>
            ))}
          </div>
        ) : (
          <div className="example-cards">
            {GENERAL_PROMPTS.map((item, index) => (
              <button
                key={index}
                className="example-card"
                onClick={() => handleExampleClick(item.text)}
              >
                <span className="example-text">{item.text}</span>
                <span className="example-arrow">→</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // 채팅 패널 렌더링
  const renderChatPanel = () => (
    <div className="chat-panel">
      {/* 진행 상태 바 */}
      {currentState === 'info_collection' && messages[messages.length - 1]?.progress && (
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${messages[messages.length - 1].progress?.percent || 0}%` }}
          />
          <span className="progress-text">
            {messages[messages.length - 1].progress?.current} / {messages[messages.length - 1].progress?.total}
          </span>
        </div>
      )}

      {/* 메시지 영역 */}
      <div className="messages-container">
        {currentState === 'welcome' && messages.length === 0 ? (
          renderWelcomeScreen()
        ) : (
          <div className="messages-list">
            {messages.map(msg => (
              <React.Fragment key={msg.id}>
                {msg.type === 'legal_guide' ? (
                  <div className="legal-guide-message">
                    <div className="legal-guide-content" dangerouslySetInnerHTML={{ __html: getLegalGuideHTML() }} />
                  </div>
                ) : (
                  <MessageBubble
                    role={msg.role}
                    content={msg.content}
                    progress={msg.progress}
                  />
                )}
                {msg.forms && msg.forms.length > 0 && (
                  <FormSelector
                    forms={msg.forms}
                    onSelect={handleFormSelect}
                  />
                )}
                {msg.downloadUrl && (
                  <div className="download-wrapper">
                    <button
                      className="download-btn"
                      onClick={() => handleDownload(msg.downloadUrl!)}
                    >
                      문서 다운로드
                    </button>
                    <button
                      className="new-form-btn"
                      onClick={handleNewSession}
                    >
                      다른 서식 작성하기
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
            {isLoading && (
              <div className="loading">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 하단 입력 영역 */}
      {currentState !== 'download' && (
        <div className="input-area">
          <div className="input-container">
            {isEditorOpen && (
              <button
                className={`input-mode-toggle ${inputMode}`}
                onClick={() => setInputMode(prev => prev === 'chat' ? 'edit' : 'chat')}
                title={inputMode === 'edit' ? '채팅 모드로 전환' : '편집 모드로 전환'}
              >
                {inputMode === 'edit' ? '편집' : '채팅'}
              </button>
            )}
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isEditorOpen && inputMode === 'edit'
                  ? "수정 요청을 입력하세요... (예: 이름을 홍길동으로 변경)"
                  : currentState === 'chatting' || (isEditorOpen && inputMode === 'chat')
                  ? "상담 또는 서식 검색... (예: 내용증명 찾아줘)"
                  : "메시지를 입력하세요..."
              }
              disabled={isLoading}
            />
            <button
              className="send-btn"
              onClick={() => handleSend()}
              disabled={isLoading || !input.trim()}
            >
              <svg viewBox="0 0 24 24" fill="none" className="send-icon">
                <path d="M7 11L12 6L17 11M12 18V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <p className="input-hint">로콜 AI는 법률 서식 작성을 도와드립니다.</p>
        </div>
      )}
    </div>
  );

  // 에디터 패널 플레이스홀더
  const renderEditorPlaceholder = () => (
    <div className="editor-placeholder">
      <div className="placeholder-content">
        <div className="placeholder-icon">📄</div>
        <h3>서식을 선택하세요</h3>
        <p>왼쪽 채팅에서 서식을 선택하면<br/>여기에서 바로 편집할 수 있습니다.</p>
      </div>
    </div>
  );

  // 모바일 레이아웃: 탭 방식
  if (isMobile) {
    return (
      <div className="chat-layout mobile">
        {/* 모바일 헤더 */}
        <div className="mobile-header">
          <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰
          </button>
          <span className="mobile-title">로콜 AI</span>
          <div className="mobile-header-actions">
            {isEditorOpen && (
              <button
                className="toggle-view-btn"
                onClick={() => setIsEditorOpen(!isEditorOpen)}
              >
                {isEditorOpen ? '채팅' : '서식'}
              </button>
            )}
            <a href="https://www.lawcall.co.kr" className="mobile-home-btn" title="로콜 홈">
              <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                <path d="M3 12L12 3L21 12M5 10V20H10V14H14V20H19V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>

        {/* 사이드바 (모바일) */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <button className="new-chat-btn" onClick={handleNewSession}>
              <span className="plus-icon">+</span>
              <span>새 상담</span>
            </button>
            <button className="toggle-btn" onClick={() => setSidebarOpen(false)}>
              ✕
            </button>
          </div>
          <div className="sidebar-content">
            <div className="sidebar-section">
              <div className="section-title">최근 상담</div>
              {sessionId && (
                <div className="chat-item active">
                  <span className="chat-title">현재 상담</span>
                </div>
              )}
              {!sessionId && (
                <div className="empty-state">상담 내역이 없습니다</div>
              )}
            </div>
          </div>
          <div className="sidebar-footer">
            <div className="brand-info">
              <span className="brand-name">로콜 AI</span>
            </div>
            <div className={`user-badge${isMaster ? ' master' : isProfessional ? ' pro' : ''}`}>
              {isMaster ? '마스터' : isProfessional ? '법률전문가' : '일반회원'}
            </div>
            <a href="https://www.lawcall.co.kr" className="home-link-btn">
              ← 로콜 홈으로 돌아가기
            </a>
          </div>
        </aside>

        {/* 메인 콘텐츠 - 탭 전환 */}
        <main className="main-area">
          {isEditorOpen ? (
            <FormEditor
              content={editorContent}
              formName={selectedFormName}
              formId={selectedFormId || undefined}
              onContentChange={setEditorContent}
              onAIEditRequest={handleAIEditRequest}
              onDownload={handleEditorDownload}
              isLoading={isEditorLoading}
              isDownloadReady={!!editorDownloadUrl}
            />
          ) : (
            renderChatPanel()
          )}
        </main>
      </div>
    );
  }

  // 데스크톱 레이아웃: Split Pane (Canvas 스타일)
  return (
    <div className="chat-layout desktop">
      {/* 좌측 사이드바 */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="new-chat-btn" onClick={handleNewSession}>
            <span className="plus-icon">+</span>
            <span>새 상담</span>
          </button>
          <button className="toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <span className="toggle-icon">{sidebarOpen ? '◀' : '▶'}</span>
          </button>
        </div>

        <div className="sidebar-content">
          <div className="sidebar-section">
            <div className="section-title">최근 상담</div>
            {sessionId && (
              <div className="chat-item active">
                <span className="chat-title">현재 상담</span>
              </div>
            )}
            {!sessionId && (
              <div className="empty-state">상담 내역이 없습니다</div>
            )}
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="brand-info">
            <span className="brand-name">로콜 AI</span>
          </div>
          <div className={`user-badge${isMaster ? ' master' : isProfessional ? ' pro' : ''}`}>
            {isMaster ? '마스터' : isProfessional ? '법률전문가' : '일반회원'}
          </div>
          <a href="https://www.lawcall.co.kr" className="home-link-btn">
            ← 로콜 홈으로 돌아가기
          </a>
        </div>
      </aside>

      {/* 메인 영역: 서식 선택 전 전체 너비 / 선택 후 Canvas 분할 */}
      <main className="main-area canvas-layout">
        {!sidebarOpen && (
          <button className="sidebar-open-btn" onClick={() => setSidebarOpen(true)}>
            <span className="menu-icon">☰</span>
          </button>
        )}

        {isEditorOpen ? (
          // 서식이 열렸을 때: 채팅(사이드) + 캔버스(메인)
          <Allotment defaultSizes={[30, 70]} minSize={280}>
            {/* 좌측: 채팅 (사이드) */}
            <Allotment.Pane>
              {renderChatPanel()}
            </Allotment.Pane>

            {/* 우측: 서식 에디터 (메인) */}
            <Allotment.Pane>
              <div className="editor-panel">
                <FormEditor
                  content={editorContent}
                  formName={selectedFormName}
                  formId={selectedFormId || undefined}
                  onContentChange={setEditorContent}
                  onAIEditRequest={handleAIEditRequest}
                  onDownload={handleEditorDownload}
                  isLoading={isEditorLoading}
                  isDownloadReady={!!editorDownloadUrl}
                />
              </div>
            </Allotment.Pane>
          </Allotment>
        ) : (
          // 서식이 안 열렸을 때: 전체 너비 채팅
          renderChatPanel()
        )}
      </main>
    </div>
  );
};

export default ChatWindow;
